import { useMutation, useQuery } from 'react-query';
import { baseApi } from 'src/apis/axios';
import {
	getDriveForJobseeker,
	getMyDriveStatus,
	getUpcomingDrivesForJobseeker,
	getPublicDrive,
	getPublicUpcomingDrives,
	createDriveRegistrationApplication,
	getAllDrives,
	getDriveCandidates,
	getDriveCompanies,
	getDrivePositionApplicants,
	getDrivePositions,
	getDriveRegistrationApplication,
} from 'src/apis/drive';
import { API_ENDPOINT, DRIVE_API_REFRESH_INTERVAL } from 'src/config/env.config';
import {
	BaseAPIResponse,
	CreateDriveRegistrationApplicationResponse,
	GetAllDrivesAPIResponse,
	GetDriveCandidatesAPIResponse,
	GetDriveCompaniesAPIInput,
	GetDriveCompaniesAPIResponse,
	GetDrivePositionApplicantsAPIResponse,
	GetDrivePositionsAPIInput,
	GetDrivePositionsAPIResponse,
	GetDriveRegistrationApplicationResponse,
	UserFiltersForApis,
} from 'types';

const baseUrl = `${API_ENDPOINT}/drive`;

export const useGetAllDrivesQuery = (queryData?: {
	onComplete?: (data: GetAllDrivesAPIResponse) => void;
	onSettled?: () => void;
	showInternalDrives?: boolean;
}) => {
	return useQuery({
		queryKey: ['getAllDrives'],
		queryFn: async () =>
			(await getAllDrives({ showInternalDrives: queryData?.showInternalDrives })) as GetAllDrivesAPIResponse,
		staleTime: 0,
		onSuccess(data) {
			queryData?.onComplete && queryData.onComplete(data);
		},
		onSettled: () => {
			queryData?.onSettled && queryData.onSettled();
		},
	});
};

export const useGetDriveCompaniesQuery = (queryData?: {
	onSettled?: () => void;
	data?: GetDriveCompaniesAPIInput;
	onComplete?: (data: GetDriveCompaniesAPIResponse) => void;
}) => {
	return useQuery<GetDriveCompaniesAPIResponse>({
		queryKey: ['getDriveCompanies', queryData?.data],
		queryFn: async () => (await getDriveCompanies(queryData?.data)) as GetDriveCompaniesAPIResponse,
		staleTime: 0,
		onSuccess(data) {
			queryData?.onComplete && queryData.onComplete(data);
		},
		onSettled: () => {
			queryData?.onSettled && queryData.onSettled();
		},
	});
};

export const useGetDrivePositionsQuery = ({
	onSettled,
	onComplete,
	data,
	enabled = true,
}: {
	onSettled?: () => void;
	data?: GetDrivePositionsAPIInput;
	onComplete?: (data: GetDrivePositionsAPIResponse) => void;
	enabled?: boolean;
} = {}) => {
	return useQuery<GetDrivePositionsAPIResponse>({
		queryKey: ['getDrivePositions', data],
		queryFn: async () => {
			return (await getDrivePositions(data)) as GetDrivePositionsAPIResponse;
		},
		enabled,
		staleTime: 0,
		onSuccess: response => {
			onComplete?.(response);
		},
		onSettled: () => {
			onSettled?.();
		},
	});
};

export const useGetDriveCandidatesQuery = (queryData?: {
	onSettled?: () => void;
	data?: UserFiltersForApis & { page: number; limit?: number; position?: string };
	onComplete?: (data: GetDriveCandidatesAPIResponse) => void;
}) => {
	return useQuery<GetDriveCandidatesAPIResponse>({
		queryKey: ['getDriveCandidates', queryData?.data],
		refetchInterval: DRIVE_API_REFRESH_INTERVAL,
		queryFn: async () => (await getDriveCandidates(queryData?.data)) as GetDriveCandidatesAPIResponse,
		staleTime: 0,
		onSuccess(data) {
			queryData?.onComplete && queryData.onComplete(data);
		},
		onSettled: () => {
			queryData?.onSettled && queryData.onSettled();
		},
	});
};

export const useGetDrivePositionApplicantsQuery = (queryData?: {
	onSettled?: () => void;
	data?: UserFiltersForApis & { page: number; limit?: number; position?: string };
	onComplete?: (data: GetDrivePositionApplicantsAPIResponse) => void;
}) => {
	return useQuery<GetDrivePositionApplicantsAPIResponse>({
		queryKey: ['getDrivePositionApplicants', queryData?.data],
		refetchInterval: DRIVE_API_REFRESH_INTERVAL,
		queryFn: async () => (await getDrivePositionApplicants(queryData?.data)) as GetDrivePositionApplicantsAPIResponse,
		staleTime: 0,
		onSuccess(data) {
			queryData?.onComplete && queryData.onComplete(data);
		},
		onSettled: () => {
			queryData?.onSettled && queryData.onSettled();
		},
	});
};

export const useCreateDriveRegistrationApplicationMutation = ({
	onSuccess,
	onError,
}: {
	onSuccess?: (data: CreateDriveRegistrationApplicationResponse) => void;
	onError?: (e: Error) => void;
}) => {
	return useMutation(createDriveRegistrationApplication, {
		onSuccess: async data => {
			// after test remove this
			// await baseApi.post(`${baseUrl}/test-send-reminder`)
			// keep this
			onSuccess && onSuccess(data as CreateDriveRegistrationApplicationResponse);
		},
		onError: (error: Error) => {
			onError && onError(error);
		},
	});
};

export const useGetDriveRegistrationApplication = (queryData?: {
	onSettled?: () => void;
	onComplete?: (data: GetDriveRegistrationApplicationResponse) => void;
}) => {
	return useQuery<GetDriveRegistrationApplicationResponse>({
		queryKey: ['getDriveRegistrationApplication'],
		queryFn: async () => (await getDriveRegistrationApplication()) as GetDriveRegistrationApplicationResponse,
		staleTime: 0,
		onSuccess(data) {
			queryData?.onComplete && queryData.onComplete(data);
		},
		onSettled: () => {
			queryData?.onSettled && queryData.onSettled();
		},
	});
};

export const useGetUpcomingDrivesForJobseeker = (enabled = true) =>
	useQuery({ queryKey: ['getUpcomingDrivesForJobseeker'], queryFn: getUpcomingDrivesForJobseeker, enabled, staleTime: 0, refetchOnMount: 'always' });

export const useGetDriveForJobseeker = (driveId?: string, enabled = true) =>
	useQuery({ queryKey: ['getDriveForJobseeker', driveId], queryFn: () => getDriveForJobseeker(driveId as string), enabled: !!driveId && enabled, staleTime: 0, refetchOnMount: 'always' });

export const useGetPublicUpcomingDrives = (enabled = true) =>
	useQuery({ queryKey: ['getPublicUpcomingDrives'], queryFn: getPublicUpcomingDrives, enabled, staleTime: 0 });

export const useGetPublicDrive = (driveId?: string, enabled = true) =>
	useQuery({ queryKey: ['getPublicDrive', driveId], queryFn: () => getPublicDrive(driveId as string), enabled: !!driveId && enabled, staleTime: 0 });

export const useGetMyDriveStatus = (driveId?: string) =>
	useQuery({ queryKey: ['getMyDriveStatus', driveId], queryFn: () => getMyDriveStatus(driveId as string), enabled: !!driveId, staleTime: 0, refetchOnMount: 'always' });
